import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Calculator, ArrowRight, RotateCcw, Clock, CheckCircle2, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { PUNTOS_EUR, CARNET_EUR } from "@/lib/courses";
import PictureImg from "@/components/PictureImg";

const OPTIONS = [
  {
    value: "parcial",
    label: "Aún tengo puntos y quiero recuperar algunos",
    hint: "Recuperación voluntaria · Tipo A · 1 día + 3 h online",
  },
  {
    value: "total",
    label: "He perdido TODOS mis puntos (carta de pérdida de vigencia)",
    hint: "Necesitas volver a conducir · Tipo B1",
  },
  {
    value: "judicial",
    label: "Me lo exige una sentencia judicial",
    hint: "Recuperación por vía judicial · Tipos B2 y B3",
  },
  {
    value: "ciclo",
    label: "Hice el curso total pero suspendí el examen de Tráfico",
    hint: "Ciclo adicional de 4 horas para volver a examinarte",
  },
];

const HORARIO_PARCIAL = "/assets/horario-curso-recuperacion-puntos-vigo.webp";
const HORARIO_TOTAL = "/assets/horario-curso-recuperacion-carnet-vigo.webp";

const RESULTS = {
  parcial: {
    title: "Curso parcial — Tipo A",
    hours: "1 día + 3 h online",
    headline: "Recuperas hasta 4 puntos",
    price: PUNTOS_EUR,
    days: "1 viernes 15:00–22:30 + 3 h online flexibles",
    image: HORARIO_PARCIAL,
    imageAlt: "Horario del curso de recuperación de 4 puntos",
    notes: [
      "1 solo día presencial (viernes) + 3 h online flexibles del viernes noche al lunes noche",
      "Ideal si aún conservas saldo y quieres recuperar puntos sin pedir vacaciones",
      "Puedes realizarlo, como máximo, una vez cada 2 años. O 1 vez al año si eres profesional",
      "No requiere examen final",
    ],
  },
  total: {
    title: "Curso total — Tipo B1",
    hours: "20 horas",
    headline: "Recupera tu permiso para volver a conducir",
    price: CARNET_EUR,
    days: "3 días presenciales + 4 h online",
    image: HORARIO_TOTAL,
    imageAlt: "Horario del curso de recuperación del carnet",
    notes: [
      "Obligatorio cuando has perdido todos los puntos, para presentarte al examen",
      "Implica examen de control de conocimientos en la DGT después del curso",
      "Puedes hacer el curso durante los meses de retirada",
      "Debes estar 6 meses sin conducir (3 meses si eres profesional) antes de poder examinarte",
    ],
  },
  judicial: {
    title: "Curso por resolución judicial — Tipos B2 y B3",
    hours: "20 horas",
    headline: "Recupera tu permiso tras la condena",
    notes: [
      { tag: "TIPO B2", text: "Retirada del permiso de hasta 2 años: sin examen" },
      { tag: "TIPO B3", text: "Retirada del permiso de más de 2 años: hay que repetir las pruebas de la DGT" },
      "Te ayudamos con la documentación y los plazos",
    ],
  },
  ciclo: {
    title: "Ciclo Adicional",
    hours: "4 horas",
    headline: "Vuelve a presentarte al examen de la DGT",
    notes: [
      "Obligatorio si suspendiste (o no te presentaste a) la prueba de control de conocimientos tras el curso total",
      "Debes realizarlo antes de cada nueva convocatoria (tienes 3 en total)",
      "Es individual: lo programamos de un día para otro, sin esperar a un grupo",
      "Repasamos contigo los contenidos del examen para que apruebes esta vez",
    ],
  },
};

// Tipo de curso que se precarga en el formulario segun la respuesta de la calculadora
const COURSE_FOR_CHOICE = { parcial: "A", total: "B1", judicial: "B2", ciclo: "CICLO_ADICIONAL" };

export default function PointsCalculator() {
  const [choice, setChoice] = useState(null);

  const FORM_TARGETS = {
    parcial: "/recuperacion-de-puntos-vigo#puntos-formulario",
    total: "/curso-sensibilizacion-vigo#sin-puntos",
    judicial: "/curso-sensibilizacion-vigo#retirada-judicial",
    ciclo: "/ciclo-adicional-vigo#reserva",
  };

  const scrollToForm = () => {
    if (choice && COURSE_FOR_CHOICE[choice]) {
      window.dispatchEvent(new CustomEvent("faro:select-course", { detail: COURSE_FOR_CHOICE[choice] }));
    }
    const el = document.querySelector('[data-testid="puntos-form"]');
    if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    else window.location.href = FORM_TARGETS[choice] || "/recuperacion-de-puntos-vigo#puntos-formulario";
  };

  const result = choice ? RESULTS[choice] : null;

  return (
    <Reveal delay={0.08}>
      <div
        className="mt-8 rounded-3xl border border-[rgba(30,42,143,0.12)] bg-white p-6 sm:p-8 shadow-lg shadow-[rgba(30,42,143,0.06)]"
        data-testid="points-calculator"
      >
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-[var(--faro-blue)] text-white flex items-center justify-center">
            <Calculator size={22} />
          </div>
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-[var(--faro-red)]">Calculadora</div>
            <h3 className="font-display font-black text-2xl text-[var(--faro-text)] leading-tight">
              ¿Qué curso de puntos necesito?
            </h3>
          </div>
        </div>

        <AnimatePresence mode="wait">
          {!result ? (
            <motion.div
              key="question"
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3 }}
              className="mt-6"
            >
              <p className="text-[var(--faro-text-soft)] font-semibold mb-4">Elige tu situación:</p>
              <div className="grid grid-cols-1 gap-3">
                {OPTIONS.map((o) => (
                  <button
                    key={o.value}
                    data-testid={`points-calc-option-${o.value}`}
                    onClick={() => setChoice(o.value)}
                    className="quiz-option flex items-center justify-between"
                  >
                    <span>
                      <span className="block">{o.label}</span>
                      <span className="block text-xs font-normal text-[var(--faro-text-soft)] mt-0.5">{o.hint}</span>
                    </span>
                    <ArrowRight size={18} className="text-[var(--faro-blue)] shrink-0" />
                  </button>
                ))}
              </div>
            </motion.div>
          ) : (
            <motion.div
              key="result"
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              className="mt-6"
              data-testid="points-calc-result"
            >
              <div className="rounded-2xl bg-[var(--faro-bg)] border border-[rgba(30,42,143,0.12)] p-6">
                <div className="flex flex-wrap items-center gap-3">
                  <span className="font-display font-black text-2xl sm:text-3xl text-[var(--faro-blue)]">{result.title}</span>
                  <span className="inline-flex items-center gap-1.5 text-sm font-bold text-[var(--faro-red)] bg-[rgba(230,57,70,0.1)] px-3 py-1 rounded-full">
                    <Clock size={14} /> {result.hours}
                  </span>
                </div>
                <p className="mt-2 text-lg font-semibold text-[var(--faro-text)]">{result.headline}</p>
                <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-5 items-start">
                  <div className="flex flex-col gap-4">
                    <ul className="space-y-2">
                      {result.notes.map((n) => {
                        const isObj = typeof n === "object";
                        const key = isObj ? n.text : n;
                        const text = isObj ? n.text : n;
                        const tag = isObj ? n.tag : null;
                        return (
                          <li key={key} className="flex items-start gap-2.5 text-[var(--faro-text-soft)]">
                            <CheckCircle2 size={18} className="text-[var(--faro-blue)] mt-0.5 shrink-0" />
                            <span>
                              {tag && (
                                <span className="inline-block mr-2 align-middle text-[10px] font-black uppercase tracking-widest bg-[var(--faro-yellow)] text-[var(--faro-blue-deep)] px-2 py-0.5 rounded-full">
                                  {tag}
                                </span>
                              )}
                              {text}
                            </span>
                          </li>
                        );
                      })}
                    </ul>
                    {result.price && (
                      <div
                        data-testid="points-calc-price-block"
                        className="rounded-2xl bg-[var(--faro-blue)] text-white p-5 shadow-lg shadow-[rgba(30,42,143,0.15)]"
                      >
                        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[var(--faro-yellow)]">
                          Precio del curso
                        </div>
                        <div className="mt-1 font-display font-black text-4xl leading-none">
                          {result.price}
                        </div>
                        {result.days && (
                          <div className="mt-3 text-sm text-white/85">
                            <span className="opacity-70">Duración:</span>{" "}
                            <span className="font-semibold">{result.days}</span>
                          </div>
                        )}
                        <button
                          data-testid="points-calc-reserve-inline"
                          onClick={scrollToForm}
                          className="mt-4 w-full inline-flex items-center justify-center gap-2 bg-[var(--faro-yellow)] hover:bg-white text-[var(--faro-blue-deep)] font-black uppercase tracking-wider text-xs py-3 rounded-full transition-colors"
                        >
                          Reservar este curso <Send size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                  {result.image && (
                    <div data-testid="points-calc-schedule" className="rounded-xl overflow-hidden border border-[rgba(30,42,143,0.15)] bg-white">
                      <div className="px-3 py-2 bg-[var(--faro-blue)] text-white text-xs uppercase tracking-[0.15em] font-bold">
                        Horario del curso
                      </div>
                      <PictureImg src={result.image} alt={result.imageAlt} width={800} height={600} sizes="(min-width: 640px) 400px, 100vw" className="w-full h-auto" />
                    </div>
                  )}
                </div>
              </div>

              <div className="mt-5 flex flex-wrap gap-3">
                <button data-testid="points-calc-reserve" onClick={scrollToForm} className="btn-pill btn-primary">
                  Reservar este curso <Send size={16} />
                </button>
                <button data-testid="points-calc-reset" onClick={() => setChoice(null)} className="btn-pill btn-ghost">
                  <RotateCcw size={15} /> Empezar de nuevo
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </Reveal>
  );
}
